import { useEffect, useState } from 'react';

export interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ToastProps {
  toasts: ToastData[];
  onRemove: (id: string) => void;
}

function ToastItem({ toast, onRemove }: { toast: ToastData; onRemove: (id: string) => void }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
    const timer = setTimeout(() => {
      setIsLeaving(true);
      setTimeout(() => onRemove(toast.id), 300);
    }, 2800);
    return () => clearTimeout(timer);
  }, [toast.id, onRemove]);

  const iconMap = {
    success: 'ri-checkbox-circle-fill',
    error: 'ri-close-circle-fill',
    info: 'ri-information-fill',
  };

  const colorMap = {
    success: 'text-green-500',
    error: 'text-accent-500',
    info: 'text-primary-500',
  };

  const bgMap = {
    success: 'bg-green-50 border-green-200',
    error: 'bg-accent-50 border-accent-200',
    info: 'bg-primary-50 border-primary-200',
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-sm min-w-[280px] max-w-[400px] transition-all duration-300 ${
        bgMap[toast.type]
      } ${
        isVisible && !isLeaving
          ? 'translate-y-0 opacity-100'
          : 'translate-y-2 opacity-0'
      }`}
    >
      <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${colorMap[toast.type]}`}>
        <i className={`${iconMap[toast.type]} text-lg`}></i>
      </span>
      <span className="text-sm font-medium text-foreground-800 flex-1">{toast.message}</span>
      <button
        onClick={() => {
          setIsLeaving(true);
          setTimeout(() => onRemove(toast.id), 300);
        }}
        className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex-shrink-0"
      >
        <i className="ri-close-line text-sm"></i>
      </button>
    </div>
  );
}

export default function Toast({ toasts, onRemove }: ToastProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[200] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onRemove={onRemove} />
        </div>
      ))}
    </div>
  );
}